import { useMemo, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  businessValueCategoryLabel,
  filterBusinessValueRows,
} from '@/lib/businessValueCategories';

function formatMoney(n) {
  const value = Number(n || 0);
  return value < 1
    ? `$${value.toFixed(4)}`
    : `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTokens(n) {
  return Number(n || 0).toLocaleString();
}

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function groupLabel(row) {
  if (row.group_type === 'ticket') {
    return row.ticket_number || row.group_id;
  }
  const sid = row.session_id || row.group_id;
  if (sid.length > 18) {
    return `${sid.slice(0, 8)}…${sid.slice(-4)}`;
  }
  return sid;
}

function groupLink(row) {
  if (row.group_type === 'ticket' && row.group_id) {
    return `/tickets/${encodeURIComponent(row.group_id)}`;
  }
  if (row.group_type === 'session' && row.group_id) {
    return `/vibe-studio?session=${encodeURIComponent(row.group_id)}`;
  }
  return null;
}

function AgentBreakdown({ rows }) {
  if (!rows?.length) {
    return (
      <p className="text-xs text-muted-foreground py-2">No agent-level usage recorded.</p>
    );
  }

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="pb-2 pr-3 font-medium">Agent</th>
          <th className="pb-2 pr-3 text-right font-medium">In</th>
          <th className="pb-2 pr-3 text-right font-medium">Out</th>
          <th className="pb-2 pr-3 text-right font-medium">Total</th>
          <th className="pb-2 pr-3 text-right font-medium">Cost</th>
          <th className="pb-2 text-right font-medium">Runs</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((agent) => (
          <tr key={agent.agent_type} className="border-t border-dashed">
            <td className="py-2 pr-3 font-medium uppercase">{agent.agent_type}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{formatTokens(agent.input_tokens)}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{formatTokens(agent.output_tokens)}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{formatTokens(agent.total_tokens)}</td>
            <td className="py-2 pr-3 text-right tabular-nums text-cp-indigo">{formatMoney(agent.llm_cost)}</td>
            <td className="py-2 text-right tabular-nums">{agent.run_count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function UsageDrillDownTable({ data }) {
  const [expandedKey, setExpandedKey] = useState(null);

  const rows = useMemo(
    () => filterBusinessValueRows(data?.rows || []),
    [data?.rows],
  );

  if (!data) return null;

  if (!rows.length) {
    return (
      <Card data-testid="bv-usage-drill-down">
        <CardHeader>
          <CardTitle>Usage by ticket &amp; session</CardTitle>
          <CardDescription>
            Per-ticket ServiceNow runs and per-session Vibe chat threads in the selected period.
          </CardDescription>
        </CardHeader>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No agent token usage recorded in this range.
        </CardContent>
      </Card>
    );
  }

  const toggleKey = (key) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  return (
    <Card data-testid="bv-usage-drill-down">
      <CardHeader>
        <CardTitle>Usage by ticket &amp; session</CardTitle>
        <CardDescription>
          {data.total_rows} row{data.total_rows === 1 ? '' : 's'} with LLM or deployment activity.
          Expand a row for agent-type breakdown.
        </CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="pb-3 pr-3 font-semibold w-8" aria-hidden="true" />
              <th className="pb-3 pr-4 font-semibold">Type</th>
              <th className="pb-3 pr-4 font-semibold">Ticket / session</th>
              <th className="pb-3 pr-4 font-semibold">Category</th>
              <th className="pb-3 pr-4 text-right font-semibold">In tokens</th>
              <th className="pb-3 pr-4 text-right font-semibold">Out tokens</th>
              <th className="pb-3 pr-4 text-right font-semibold">Total</th>
              <th className="pb-3 pr-4 text-right font-semibold text-cp-indigo">LLM cost</th>
              <th className="pb-3 pr-4 text-right font-semibold">Runs</th>
              <th className="pb-3 pr-4 text-right font-semibold">Deploys</th>
              <th className="pb-3 font-semibold">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const rowKey = `${row.group_type}:${row.group_id}`;
              const isOpen = expandedKey === rowKey;
              const href = groupLink(row);

              return (
                <Fragment key={rowKey}>
                  <tr className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-3 pr-3 align-top">
                      <button
                        type="button"
                        className="rounded p-1 text-muted-foreground hover:bg-muted"
                        aria-expanded={isOpen}
                        aria-label={isOpen ? 'Collapse agent breakdown' : 'Expand agent breakdown'}
                        onClick={() => toggleKey(rowKey)}
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <Badge variant={row.group_type === 'ticket' ? 'default' : 'secondary'}>
                        {row.group_type === 'ticket' ? 'Ticket' : 'Session'}
                      </Badge>
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <div className="font-semibold">{groupLabel(row)}</div>
                      {href && (
                        <Link
                          to={href}
                          className="inline-flex items-center gap-1 text-xs text-cp-violet underline-offset-2 hover:underline"
                        >
                          Open
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                      {row.session_id && row.group_type === 'ticket' && (
                        <div className="mt-1 text-xs text-muted-foreground truncate max-w-[220px]">
                          session: {row.session_id}
                        </div>
                      )}
                    </td>
                    <td className="py-3 pr-4 align-top">{businessValueCategoryLabel(row)}</td>
                    <td className="py-3 pr-4 text-right tabular-nums align-top">
                      {formatTokens(row.input_tokens)}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums align-top">
                      {formatTokens(row.output_tokens)}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums align-top font-medium">
                      {formatTokens(row.total_tokens)}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums align-top font-semibold text-cp-indigo">
                      {formatMoney(row.llm_cost)}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums align-top">{row.run_count}</td>
                    <td className="py-3 pr-4 text-right tabular-nums align-top">{row.deploy_count || 0}</td>
                    <td className="py-3 align-top text-muted-foreground">
                      {formatWhen(row.last_activity_at)}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-muted/20">
                      <td colSpan={11} className="px-4 pb-4 pt-1">
                        <AgentBreakdown rows={row.by_agent_type} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
